"use client";

import { useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { Button } from "@offstreetparking/design-system/forms";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { useMutation, useQuery } from "@tanstack/react-query";
import Image from "next/image";
import { useEffect, useState } from "react";
import { OverlayShell } from "./OverlayShell";
import { connectToCompany, isConnectedToCompany } from "./actions";
import { isSkipped, skipForADay } from "./skip-storage";

type ConnectCompanyPromptProps = {
	companyId: number;
	companyName: string;
	logo: { url: string; altText: string };
};

export const ConnectCompanyPrompt = ({ companyId, companyName, logo }: ConnectCompanyPromptProps) => {
	const { t } = useLingui();
	const [skipped, setSkipped] = useState(true);

	useEffect(() => {
		setSkipped(isSkipped(companyId));
	}, [companyId]);

	const { data: isConnected, refetch } = useQuery({
		queryKey: ["connect-company-membership", companyId],
		enabled: !skipped,
		queryFn: () => isConnectedToCompany(),
	});

	const {
		mutate: connect,
		isPending,
		isError,
	} = useMutation({
		mutationFn: () => connectToCompany(),
		onSuccess: () => refetch(),
	});

	if (skipped || isConnected !== false) {
		return null;
	}

	return (
		<OverlayShell label={t`Connect to ${companyName}`}>
			<Image
				src={logo.url}
				width={0}
				height={0}
				sizes="160px"
				alt={logo.altText}
				style={{ maxHeight: "64px", width: "auto", maxWidth: "160px" }}
			/>
			<Heading As="h1" kind="h1" className="font-bold text-3xl leading-tight sm:text-4xl">
				{t`Connect to ${companyName}`}
			</Heading>
			<Text intent="muted">
				{t`${companyName} has a parking profile for your email. Connect to access any permits they've assigned to you.`}
			</Text>
			<Text intent="muted" size="sm">
				{t`When you connect, ${companyName} will be able to view your name, email, and the vehicles associated with any permits they issue to you.`}
			</Text>
			{isError && (
				<Alert
					type="error"
					title={t`We couldn't connect your account`}
					message={t`Please try again, and if the issue continues, contact Support.`}
				/>
			)}
			<div className="flex w-full flex-col gap-3 sm:flex-row-reverse sm:justify-center">
				<Button
					className="w-full sm:w-auto sm:min-w-40"
					intent="primary"
					size="lg"
					isLoading={isPending}
					onClick={() => connect()}
				>
					{t`Connect`}
				</Button>
				<Button
					className="w-full sm:w-auto sm:min-w-40"
					intent="secondary"
					size="lg"
					onClick={() => {
						skipForADay(companyId);
						setSkipped(true);
					}}
				>
					{t`Skip for now`}
				</Button>
			</div>
		</OverlayShell>
	);
};
